import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { nanoid } from 'nanoid';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import Plus from '@components/Icons/Plus';
import { HookResponse, ResourceType } from './common';

const getDetailPagePath: HookResponse['getDetailPagePath'] = ({ id }) => getUrl(IDENTIFIERS.DASHBOARD_GOVERNMENT_FORMS_EDIT, { slug: id });

const useGovernmentForms = ({
  search,
  resourceType,
  perPage = 10_000,
}: {
  search: string | null;
  resourceType: ResourceType;
  perPage?: number;
}): HookResponse & {
  createGovernmentFormModalOpen: boolean;
  setCreateGovernmentFormModalOpen: (v: boolean) => void;
} => {
  const [createGovernmentFormModalOpen, setCreateGovernmentFormModalOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const pathParams = useParams<{ slug?: string }>();
  const [items, setItems] = useState<HookResponse['items']>([]);
  const mostRecentRequestRef = useRef<string | null>(null);

  const { getData: getDynamicPdfTemplatesFn } = useNoodleApi(tsClient.getDynamicPdfTemplates);

  const selectedId = pathParams?.slug;

  const loadPage = useCallback(async ({ page, search: newSearch }: {search: string | null, page: number }): Promise<void> => {
    if (resourceType === 'government-forms') {
      setCurrentPage(page);
      const thisRequestId = nanoid();
      mostRecentRequestRef.current = thisRequestId;

      setIsFetching(true);
      const response = await getDynamicPdfTemplatesFn({
        page,
        perPage,
        searchTerm: newSearch ?? undefined,
      });

      if (mostRecentRequestRef.current === thisRequestId) {
        if (response.data) {
          setItems(response.data.items[0].dynamicTemplateData
            .map(i => ({
              badge: {
                color: i.status === 'published' ? 'var(--color-success)' : 'var(--color-gray-75)',
                label: `Version ${i.version}`,
              },
              bottomLine: i.description ? `${i.slug} · ${i.description}` : i.slug,
              href: getUrl(IDENTIFIERS.DASHBOARD_GOVERNMENT_FORMS_EDIT, { slug: i.slug }),
              id: i.slug,
              label: i.name,
            })));
          setNumPages(response.data.numPages);
        }
        setCreateGovernmentFormModalOpen(false);
        setIsFetching(false);
      }
    } else {
      setItems([]);
    }
  }, [
    resourceType,
    getDynamicPdfTemplatesFn,
    perPage,
  ]);

  useEffect(() => {
    loadPage({ page: 1, search });
  }, [loadPage, search]);

  const headerActions = [{
    icon: Plus,
    label: 'Create Government Form',
    onClick: () => setCreateGovernmentFormModalOpen(true),
  }];

  const pagination: HookResponse['pagination'] = {
    numPages,
    onChangePage: (newPage) => {
      loadPage({ page: newPage, search });
    },
    page: currentPage,
  };

  return {
    createGovernmentFormModalOpen,
    getDetailPagePath,
    headerActions,
    ifNoItemsText: 'No government forms yet',
    isFetchingList: isFetching,
    items,
    pagination,
    reloadList: () => loadPage({ page: 1, search }),
    selectedId,
    setCreateGovernmentFormModalOpen,
  };
};

export default useGovernmentForms;
